import { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "", company: "", country: "", email: "", phone: "",
  products: "", packaging: "", quantity: "", destination: "", message: "",
};

export default function RequestQuoteModal({ open, onClose, defaultProduct = "" }) {
  const [form, setForm] = useState({ ...emptyForm, products: defaultProduct });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, products: defaultProduct });
      setDone(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, defaultProduct]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "quote" });
      setDone(true);
      toast.success("Inquiry received. Our export team will respond within 24 business hours.");
    } catch {
      toast.error("Could not submit. Please email info@deeprootsspice.com directly.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div
      className="fixed inset-0 z-[60] bg-brand-ink/85 backdrop-blur-md flex items-start md:items-center justify-center overflow-y-auto py-10 px-4"
      data-testid="quote-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="bg-brand-ivory w-full max-w-3xl border border-brand-gold/40 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center border border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory transition-colors"
          data-testid="quote-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-green/10 border border-brand-green flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-brand-green" />
            </div>
            <h3 className="font-serif text-3xl text-brand-charcoal mb-3">Inquiry received</h3>
            <p className="text-brand-charcoal/70 max-w-md mx-auto">
              Thank you. Our export team will review your requirements and respond within 24 business hours.
            </p>
            <button onClick={onClose} className="mt-8 luxe-btn-outline text-brand-charcoal border-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory" data-testid="quote-modal-done">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-12">
            <div className="mb-8">
              <span className="overline">Request a Quotation</span>
              <h3 className="font-serif text-3xl md:text-4xl mt-3 text-brand-charcoal">Tell us about your requirements</h3>
              <span className="divider-gold" />
              <p className="text-brand-charcoal/60 text-sm max-w-lg">Complete the form and our export team will respond with pricing, samples and shipping options tailored to your market.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Full Name*</label>
                <input required value={form.name} onChange={set("name")} className="input-editorial" data-testid="quote-name" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Company</label>
                <input value={form.company} onChange={set("company")} className="input-editorial" data-testid="quote-company" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Email*</label>
                <input required type="email" value={form.email} onChange={set("email")} className="input-editorial" data-testid="quote-email" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Phone</label>
                <input value={form.phone} onChange={set("phone")} className="input-editorial" data-testid="quote-phone" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Your Country</label>
                <input value={form.country} onChange={set("country")} className="input-editorial" data-testid="quote-country" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Destination Port / Country</label>
                <input value={form.destination} onChange={set("destination")} className="input-editorial" data-testid="quote-destination" />
              </div>
              <div className="md:col-span-2">
                <label className="overline text-brand-charcoal/50 block mb-1">Required Products</label>
                <input placeholder="e.g. Turmeric Powder, Green Cardamom, Garam Masala" value={form.products} onChange={set("products")} className="input-editorial" data-testid="quote-products" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Packaging Preference</label>
                <input placeholder="25kg PP bags, retail SUP, etc." value={form.packaging} onChange={set("packaging")} className="input-editorial" data-testid="quote-packaging" />
              </div>
              <div>
                <label className="overline text-brand-charcoal/50 block mb-1">Quantity</label>
                <input placeholder="e.g. 5 MT" value={form.quantity} onChange={set("quantity")} className="input-editorial" data-testid="quote-quantity" />
              </div>
              <div className="md:col-span-2">
                <label className="overline text-brand-charcoal/50 block mb-1">Message</label>
                <textarea rows={3} value={form.message} onChange={set("message")} className="input-editorial resize-none" data-testid="quote-message" />
              </div>
            </div>

            <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <p className="text-[12px] text-brand-charcoal/50">By submitting, you agree to be contacted by our export team regarding your inquiry.</p>
              <button type="submit" disabled={busy} className="luxe-btn-primary disabled:opacity-60" data-testid="quote-submit">
                {busy ? "Sending..." : "Send Inquiry"} <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
